import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Nav } from "./Nav";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SupportBanner } from "@/features/support/SupportBanner";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { COMPOSER_URL } from "@/lib/links";

/**
 * The shell every signed-in page sits in.
 *
 * A fixed left rail on a wide screen and a disclosure on a narrow one. The rail
 * is not collapsible on desktop: this is a tool people use for twenty minutes
 * at a time between lessons, and a navigation that has to be reopened is a
 * navigation that gets learned twice.
 *
 * White ground and one hairline between the rail and the page. The rail used
 * to be a second shade of paper with white pills on it, which made the page
 * and the navigation two competing surfaces; on white, the only thing carrying
 * colour is the item you are on.
 */
export function Layout() {
  const t = useT();
  const navigate = useNavigate();
  const { user, institution, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50
                   focus:rounded-control focus:bg-white focus:px-4 focus:py-2 focus:text-ink focus:shadow-pop"
      >
        {t("nav.skipToContent")}
      </a>

      <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b border-line bg-canvas px-4">
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="side-nav"
          aria-label={t("nav.menu")}
          className="-ml-1 rounded-control p-2 text-ink-soft hover:bg-surface-muted hover:text-ink md:hidden"
        >
          <span aria-hidden="true">☰</span>
        </button>

        {/*
          The institution's name is the one thing that tells a teacher which
          studio they are in, so it gets room before anything else does. The
          wordmark does not shrink and does not wrap; the name truncates only
          when there is genuinely nothing left.

          The wordmark is set in Inter, not the serif: the display face survives
          on the sign-in card and nowhere else in this app, because a serif on
          a header reads as a publication rather than an instrument.
        */}
        <div className="flex min-w-0 flex-1 items-baseline gap-3">
          <span className="shrink-0 whitespace-nowrap text-[15px] font-semibold tracking-[-0.01em] text-ink">
            {t("app.name")}
          </span>
          {institution ? (
            <span className="min-w-0 truncate text-[14px] text-ink-muted">{institution.name}</span>
          ) : null}
        </div>

        {/*
          The other front door, named as an action and explained in one line.
          A teacher who was handed a Backstage link has been told nothing about
          where the scenes are written; the header is the one place they look
          on every page.
        */}
        {/*
          German runs about a third longer than English, and this line ran
          straight across the wordmark and pushed the institution's name out of
          the header on every German screen (live run, 2026-09-09). The hint is
          shorter now, it is allowed to shrink, and it only appears once there
          is a screen wide enough to hold it.
        */}
        <a
          href={COMPOSER_URL}
          className="hidden min-w-0 max-w-[18rem] shrink flex-col items-end rounded-control px-2 py-1
                     hover:bg-surface-muted lg:flex"
        >
          <span className="whitespace-nowrap text-[13px] font-medium text-ink">
            {t("nav.openComposer")}
            <span aria-hidden="true" className="ml-1 text-ink-faint">↗</span>
          </span>
          <span className="truncate text-[12px] text-ink-muted">{t("nav.openComposerHint")}</span>
        </a>

        <LanguageSwitcher />

        <div className="flex items-center gap-3 border-l border-line pl-3">
          <div className="hidden text-right sm:block">
            <p className="text-[13px] font-medium leading-4 text-ink">{user?.displayName}</p>
            <p className="text-[12px] leading-4 text-ink-muted">{user ? t(`role.${user.role}`) : null}</p>
          </div>
          <button
            type="button"
            onClick={async () => {
              await signOut();
              navigate("/login", { replace: true });
            }}
            className="rounded-control px-2.5 py-1.5 text-[13px] text-ink-muted hover:bg-surface-muted hover:text-ink"
          >
            {t("nav.signOut")}
          </button>
        </div>
      </header>

      {/*
        In the shell, not on a page. An owner who approved something on Tuesday
        and forgot has not consented on Thursday in any sense worth the word.
      */}
      <SupportBanner />

      <div className="flex">
        <aside
          id="side-nav"
          className={`w-60 shrink-0 border-r border-line bg-canvas md:sticky md:top-14
                      md:block md:h-[calc(100vh-3.5rem)] md:overflow-y-auto ${
                        menuOpen ? "block" : "hidden"
                      }`}
        >
          <Nav />
        </aside>

        <main id="main" className="min-w-0 flex-1 px-6 py-8">
          <Outlet />
        </main>
      </div>

    </div>
  );
}
